/**
 * Admin Jobs Page
 * Monitor scraper jobs, CVE sync jobs, and download jobs
 */

import { useState, useEffect } from 'react';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import { Link } from 'react-router-dom';
import { getApiUrl } from '../../config/api';
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';

interface Job {
  id: number;
  job_type: string;
  software_id: number | null;
  software_name: string | null;
  status: 'pending' | 'running' | 'completed' | 'failed';
  priority: number;
  scheduled_at: string;
  started_at: string | null;
  completed_at: string | null;
  duration_ms: number | null;
  result: string | null;
  error_message: string | null;
  triggered_by: 'cron' | 'manual' | 'api';
}

interface JobStats {
  overview: {
    total_jobs: number;
    completed: number;
    failed: number;
    running: number;
    pending: number;
    avg_duration_ms: number;
  };
  by_type: Array<{
    job_type: string;
    count: number;
    failed_count: number;
  }>;
  recent_failures: Array<{
    id: number;
    job_type: string;
    software_name: string | null;
    error_message: string;
    completed_at: string;
  }>;
}

export default function AdminJobs() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [stats, setStats] = useState<JobStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
      });

      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('job_type', typeFilter);

      const [jobsRes, statsRes] = await Promise.all([
        fetchWithAuth(getApiUrl(`api/v1/admin/jobs?${params}`)),
        fetchWithAuth(getApiUrl('api/v1/admin/jobs/stats')),
      ]);

      const jobsData = await jobsRes.json();
      const statsData = await statsRes.json();

      if (jobsData.success) {
        setJobs(jobsData.data);
        setTotalPages(jobsData.pagination.total_pages);
      }
      if (statsData.success) {
        setStats(statsData.data);
      }
    } catch (error) {
      console.error('Error loading jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [currentPage, statusFilter, typeFilter]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-400 bg-green-900/20 border-green-500/30';
      case 'failed':
        return 'text-red-400 bg-red-900/20 border-red-500/30';
      case 'running':
        return 'text-blue-400 bg-blue-900/20 border-blue-500/30';
      case 'pending':
        return 'text-yellow-400 bg-yellow-900/20 border-yellow-500/30';
      default:
        return 'text-gray-400 bg-gray-800/30 border-gray-700';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="w-5 h-5" />;
      case 'failed':
        return <XCircleIcon className="w-5 h-5" />;
      case 'running':
        return <ArrowPathIcon className="w-5 h-5 animate-spin" />;
      case 'pending':
        return <ClockIcon className="w-5 h-5" />;
      default:
        return null;
    }
  };

  const formatDuration = (ms: number | null) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyber-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading jobs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-100">Jobs</h1>
          <p className="text-gray-400 mt-1">Monitor scraper and sync job execution</p>
        </div>
        <button
          onClick={loadData}
          className="flex items-center gap-2 px-4 py-2 bg-cyber-blue-500/10 text-cyber-blue-400 border border-cyber-blue-500/30 rounded hover:bg-cyber-blue-500/20 transition-colors"
        >
          <ArrowPathIcon className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-400 mb-1">Total Jobs</div>
            <div className="text-2xl font-bold text-gray-100">{stats.overview.total_jobs}</div>
          </div>
          <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
            <div className="text-sm text-green-400 mb-1">Completed</div>
            <div className="text-2xl font-bold text-green-400">{stats.overview.completed}</div>
          </div>
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
            <div className="text-sm text-red-400 mb-1">Failed</div>
            <div className="text-2xl font-bold text-red-400">{stats.overview.failed}</div>
          </div>
          <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
            <div className="text-sm text-blue-400 mb-1">Running</div>
            <div className="text-2xl font-bold text-blue-400">{stats.overview.running}</div>
          </div>
          <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-400 mb-1">Avg Duration</div>
            <div className="text-2xl font-bold text-gray-100">
              {formatDuration(stats.overview.avg_duration_ms)}
            </div>
          </div>
        </div>
      )}

      {/* Job Types */}
      {stats && stats.by_type.length > 0 && (
        <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-100 mb-4">Jobs by Type</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.by_type.map((type) => (
              <div key={type.job_type} className="text-sm">
                <div className="text-gray-400 capitalize">{type.job_type}:</div>
                <div className="text-gray-100 font-semibold">
                  {type.count}
                  {type.failed_count > 0 && (
                    <span className="ml-2 text-red-400">({type.failed_count} failed)</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4">
        <div className="flex items-center gap-4">
          <FunnelIcon className="w-5 h-5 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-gray-900/50 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:border-cyber-blue-500"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="running">Running</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 bg-gray-900/50 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:border-cyber-blue-500"
          >
            <option value="">All Types</option>
            <option value="scraper">Scraper</option>
            <option value="cve-sync">CVE Sync</option>
            <option value="download">Download</option>
          </select>
        </div>
      </div>

      {/* Jobs Table */}
      <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-100 mb-4">Recent Jobs</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 border-b border-gray-700">
                <th className="pb-2 font-medium">ID</th>
                <th className="pb-2 font-medium">Type</th>
                <th className="pb-2 font-medium">Software</th>
                <th className="pb-2 font-medium">Status</th>
                <th className="pb-2 font-medium">Duration</th>
                <th className="pb-2 font-medium">Scheduled</th>
                <th className="pb-2 font-medium">Triggered By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {jobs.map((job) => (
                <tr key={job.id} className="hover:bg-gray-800/30">
                  <td className="py-3">
                    <Link
                      to={`/admin/jobs/${job.id}`}
                      className="text-cyber-blue-400 hover:text-cyber-blue-300"
                    >
                      #{job.id}
                    </Link>
                  </td>
                  <td className="py-3">
                    <span className="capitalize text-gray-300">{job.job_type}</span>
                  </td>
                  <td className="py-3 text-gray-300">{job.software_name || 'N/A'}</td>
                  <td className="py-3">
                    <span className={`flex items-center gap-2 text-xs px-2 py-1 border rounded w-fit ${getStatusColor(job.status)}`}>
                      {getStatusIcon(job.status)}
                      {job.status}
                    </span>
                  </td>
                  <td className="py-3 text-gray-300">{formatDuration(job.duration_ms)}</td>
                  <td className="py-3 text-gray-400 text-xs">
                    {new Date(job.scheduled_at).toLocaleString()}
                  </td>
                  <td className="py-3">
                    <span className="text-xs px-2 py-1 bg-gray-700 text-gray-300 rounded">
                      {job.triggered_by}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
          <span className="text-sm text-gray-400">
            Page {currentPage} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-gray-700 text-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 bg-gray-700 text-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Recent Failures */}
      {stats && stats.recent_failures.length > 0 && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-red-400 mb-4">Recent Failures</h3>
          <div className="space-y-3">
            {stats.recent_failures.map((failure) => (
              <div key={failure.id} className="bg-red-900/10 border border-red-500/20 rounded p-3">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <Link
                      to={`/admin/jobs/${failure.id}`}
                      className="text-red-400 hover:text-red-300 font-medium"
                    >
                      Job #{failure.id}
                    </Link>
                    <span className="text-gray-400 text-sm ml-2">
                      ({failure.job_type}) {failure.software_name || ''}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {new Date(failure.completed_at).toLocaleString()}
                  </span>
                </div>
                <p className="text-sm text-red-200">{failure.error_message}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
